import React, { useState, useEffect } from 'react';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeArtifactStateTransitionCreate,
} from '../../types/primeModel';
import { PrimeArtifact, CreatePrimeArtifact } from '../../types/primeArtifact';
import { PrimeProcess } from '../../types/primeModel';
import { ArtifactService } from '../../services/api/artifactService';
import { DemandType, DemandFlowType } from '../../types/enums';
import { ArtifactStateTransitionType } from '../../types/enums';
import { ArtifactWithParameters } from '../../types/artifacts';
import styles from './TransitionCreationFlow.module.css';
import { useArtifactTree } from '../../contexts';
import { useUser } from '../../contexts/UserContext';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { ArtifactItemState } from '../../types/artifactItems';

import { PrimeArtifactStateCreate } from '../../types/primeArtifactState';
import {
  getStateType,
  areStatesSameType
} from '../../types/standardStates';

export type CreationStep = 'artifact' | 'transitionType' | 'state' | 'transition';

interface TransitionCreationFlowProps {
  process: PrimeProcess;
  onTransitionCreated: (transition: PrimeArtifactStateTransition) => void;
  onCancel: () => void;
  skipProcessSelection?: boolean;
}

const TransitionCreationFlow: React.FC<TransitionCreationFlowProps> = ({
  process,
  onTransitionCreated,
  onCancel,
  skipProcessSelection = false,
}) => {
  const { selectedTree } = useArtifactTree();
  const { selectedCompany } = useUser();

  const [currentStep, setCurrentStep] = useState<CreationStep>(
    skipProcessSelection ? 'artifact' : 'artifact'
  );
  const [artifacts, setArtifacts] = useState<PrimeArtifact[]>([]);
  const [selectedArtifact, setSelectedArtifact] = useState<PrimeArtifact | null>(null);
  const [selectedArtifactStates, setSelectedArtifactStates] = useState<PrimeArtifactState[]>([]);
  const [selectedFromState, setSelectedFromState] = useState<PrimeArtifactState | null>(null);
  const [selectedToState, setSelectedToState] = useState<PrimeArtifactState | null>(null);
  const [newTransition, setNewTransition] = useState<Partial<PrimeArtifactStateTransition>>({
    default_transition_type: ArtifactStateTransitionType.OPERATIONAL,
    transition_parameters: {},
    validation_rules: {},
    demand_type: DemandType.TARGET,
    demand_flow_type: DemandFlowType.DRUM,
    is_secondary: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State creation form
  const [showStateForm, setShowStateForm] = useState(false);
  const [stateFormType, setStateFormType] = useState<'input' | 'output' | 'resource'>('input');
  const [newState, setNewState] = useState<PrimeArtifactStateCreate>({
    name: '',
    description: undefined,
    prime_artifact_ref: '',
    is_required: true,
    state_type: ArtifactStateTransitionType.OPERATIONAL,
    demand_type: DemandType.TARGET,
    demand_flow_type: DemandFlowType.DRUM,
    is_initial: false,
    is_final: false,
    is_improvement: false,
    is_resource_active: false,
    state_metadata: {},
    state_rules: {},
    state_parameters: {},
    validation_rules: {},
  });

  // Default artifact type to use when creating new artifacts
  const defaultArtifactType = {
    id: '123e4567-e89b-12d3-a456-426614174000', // Using a valid UUID format
    name: 'Default Type',
  };

  // Form state for creating a new artifact
  const [showArtifactForm, setShowArtifactForm] = useState(false);
  const [newArtifact, setNewArtifact] = useState<CreatePrimeArtifact>({
    name: '',
    description: undefined,
    artifact_type_id: defaultArtifactType.id,
    artifact_type_name: defaultArtifactType.name,
    parent_ref: undefined,
    parent_name: undefined,
  });

  // Fetch artifacts on component mount
  useEffect(() => {
    const fetchArtifacts = async () => {
      try {
        setLoading(true);
        const artifacts = await ArtifactService.getAll(selectedTree?.id || '', undefined, selectedCompany?.id);
        const primeArtifacts = artifacts.map((artifact) => ({
          id: artifact.id,
          name: artifact.name,
          description: artifact.description || undefined,
          artifact_type_id: artifact.artifact_type_id || 'unknown',
          artifact_type_name: '', // We'll need to fetch this separately
          parent_ref: artifact.parent?.id || undefined,
          parent_name: '', // We'll need to fetch this separately
          children: [],
          created_at: artifact.created_at,
          updated_at: artifact.updated_at || undefined,
          prime_artifact_id: artifact.prime_artifact_id || undefined,
        }));
        setArtifacts(primeArtifacts);
      } catch (err) {
        setError('Failed to fetch artifacts');
        console.error('Error fetching artifacts:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchArtifacts();
  }, [selectedTree?.id, selectedCompany?.id]);

  // Fetch states when selected artifact changes
  useEffect(() => {
    if (selectedArtifact?.id) {
      const fetchArtifactStates = async () => {
        try {
          setLoading(true);
          const states = await ArtifactService.getStates(selectedArtifact.id);
          // Convert ArtifactItemState to PrimeArtifactState
          const primeStates = await Promise.all(
            states.map(async (state: ArtifactItemState) => {
              const stateData: PrimeArtifactStateCreate = {
                name: state.name,
                description: state.description || undefined,
                prime_artifact_ref: selectedArtifact.id,
                is_required: true,
                state_type: ArtifactStateTransitionType.OPERATIONAL,
                is_initial: Boolean(state.is_initial),
                is_final: Boolean(state.is_final),
                is_improvement: false,
                is_resource_active: false,
                demand_type: DemandType.TARGET,
                demand_flow_type: DemandFlowType.DRUM,
                state_metadata: state.state_metadata || {},
                state_rules: {},
                state_parameters: {},
                validation_rules: {},
              };
              const createdState =
                await PrimeArtifactService.createPrimeArtifactState(stateData);
              return createdState;
            })
          );

          // Update state selections with the newly created prime states
          setSelectedArtifactStates(primeStates);

          // Update state selections if they exist
          if (selectedFromState) {
            const matchingFromState = primeStates.find(
              (s: PrimeArtifactState) => s.name === selectedFromState.name
            );
            setSelectedFromState(matchingFromState || null);
            if (matchingFromState) {
              setNewTransition((prev) => ({ ...prev, from_state_ref: matchingFromState.id }));
            }
          }
          if (selectedToState) {
            const matchingToState = primeStates.find(
              (s: PrimeArtifactState) => s.name === selectedToState.name
            );
            setSelectedToState(matchingToState || null);
            if (matchingToState) {
              setNewTransition((prev) => ({ ...prev, to_state_ref: matchingToState.id }));
            }
          }
        } catch (err) {
          console.error('Error fetching states:', err);
          setError('Failed to fetch states');
        } finally {
          setLoading(false);
        }
      };

      void fetchArtifactStates();
    }
  }, [selectedArtifact?.id, selectedFromState, selectedToState]);

  const handleArtifactSelect = (artifact: PrimeArtifact) => {
    setSelectedArtifact(artifact);
    setNewTransition((prev) => ({ ...prev, prime_artifact_ref: artifact.id }));
    setCurrentStep('transitionType');
  };

  const handleArtifactFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewArtifact((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArtifactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!newArtifact.name.trim()) {
      setError('Artifact name is required');
      return;
    }

    if (!newArtifact.artifact_type_id) {
      setError('Artifact type is required');
      return;
    }

    try {
      setLoading(true);

      // Create the artifact data with all required fields
      const artifactData: Partial<ArtifactWithParameters> = {
        name: newArtifact.name.trim(),
        description: newArtifact.description || undefined,
        artifact_type_id: newArtifact.artifact_type_id,
        parent_ref: newArtifact.parent_ref || undefined,
        prime_artifact_id: undefined,
      };

      console.log('Submitting artifact data:', artifactData);

      const createdArtifact = await ArtifactService.create(artifactData);
      if (createdArtifact) {
        const primeArtifact: PrimeArtifact = {
          id: createdArtifact.id,
          name: createdArtifact.name,
          description: createdArtifact.description || undefined,
          artifact_type_id: createdArtifact.artifact_type_id || 'unknown',
          artifact_type_name: newArtifact.artifact_type_name || '',
          parent_ref: newArtifact.parent_ref || undefined,
          parent_name: newArtifact.parent_name || undefined,
          created_at: createdArtifact.created_at,
          updated_at: createdArtifact.updated_at || undefined,
          children: [],
          states: [],
          transitions: [],
          processes: [],
          prime_artifact_id: createdArtifact.prime_artifact_id || '',
        };

        setArtifacts((prev) => [...prev, primeArtifact]);
        setSelectedArtifact(primeArtifact);
        setNewTransition((prev) => ({ ...prev, prime_artifact_ref: primeArtifact.id }));
        setShowArtifactForm(false);
      }
      setCurrentStep('transitionType');
    } catch (err: unknown) {
      setError('Failed to create artifact');
      console.error('Error creating artifact:', err);

      // Log the detailed error response
      if ((err as { response?: { data?: unknown; status?: number } }).response) {
        const errorResponse = (err as { response: { data: unknown; status: number } }).response;
        console.error('Error response data:', errorResponse.data);
        console.error('Error response status:', errorResponse.status);

        // Display validation errors if available
        const errorData = errorResponse.data as { detail?: unknown };
        if (errorData && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail
              .map((error: unknown) => {
                const errorObj = error as { loc?: string[]; msg?: string };
                return `${errorObj.loc?.join('.') || 'field'}: ${errorObj.msg || 'validation error'}`;
              })
              .join('\n');
            setError(`Validation errors:\n${errorMessages}`);
          } else {
            setError(`Server error: ${errorData.detail}`);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStateFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newState.name.trim()) {
      setError('State name is required');
      return;
    }

    try {
      setLoading(true);

      // Create the state data with all required fields
      const stateData = {
        ...newState,
        name: newState.name.trim(),
        description: newState.description?.trim() || '',
        prime_artifact_ref: selectedArtifact?.id || '',
        is_required: true,
        state_type: ArtifactStateTransitionType.OPERATIONAL,
        demand_type: newState.demand_type as DemandType,
        demand_flow_type: newState.demand_flow_type as DemandFlowType,
        is_initial: false,
        is_final:
          stateFormType === 'output' &&
          newTransition.default_transition_type === ArtifactStateTransitionType.MATERIAL,
        is_improvement: false,
        is_resource_active: false,
        state_metadata: {},
        state_rules: {},
        state_parameters: {},
        validation_rules: {},
        improvement_ref: null,
        improvement_parameters: {},
      };

      await handleStateCreate(stateData);
      setShowStateForm(false);
      setNewState((prev) => ({ ...prev, name: '', description: '' }));
    } catch (err) {
      console.error('Error in state form submission:', err);
    } finally {
      setLoading(false);
    }
  };

  const openStateForm = (type: 'input' | 'output' | 'resource') => {
    setStateFormType(type);
    setNewState((prev) => ({
      ...prev,
      name:
        type === 'input'
          ? 'New Input State'
          : type === 'output'
            ? 'New Output State'
            : 'New Resource State',
      description:
        type === 'input'
          ? 'Description for new input state'
          : type === 'output'
            ? 'Description for new output state'
            : 'Description for new resource state',
    }));
    setShowStateForm(true);
  };

  const handleTransitionTypeSelect = (transitionType: ArtifactStateTransitionType) => {
    setNewTransition((prev) => ({ ...prev, default_transition_type: transitionType }));
    setCurrentStep('state');
  };

  const validateStateSelection = (fromState: PrimeArtifactState | null, toState: PrimeArtifactState | null): string | null => {
    if (!fromState || !toState) {
      return null; // Can't validate if both aren't selected
    }
    
    const fromStateType = getStateType(fromState.name);
    const toStateType = getStateType(toState.name);
    
    // Only validate if both states are standard states
    if (fromStateType && toStateType && fromStateType !== toStateType) {
      return `${fromState.name} (${fromStateType}) and ${toState.name} (${toStateType}) must be the same type`;
    }
    
    // Also validate using areStatesSameType
    if (!areStatesSameType(fromState.name, toState.name) && (fromStateType || toStateType)) {
      return 'States must be of the same type (both operational or both improvement)';
    }
    
    return null;
  };

  const handleFromStateSelect = (state: PrimeArtifactState) => {
    setSelectedFromState(state);
    setNewTransition((prev) => ({ ...prev, from_state_ref: state.id }));
    
    // Validate if to state is already selected
    if (selectedToState) {
      const validationError = validateStateSelection(state, selectedToState);
      if (validationError) {
        setError(validationError);
      } else {
        setError(null);
      }
    }
  };

  const handleToStateSelect = (state: PrimeArtifactState) => {
    setSelectedToState(state);
    setNewTransition((prev) => ({ ...prev, to_state_ref: state.id }));
    
    // Validate if from state is already selected
    if (selectedFromState) {
      const validationError = validateStateSelection(selectedFromState, state);
      if (validationError) {
        setError(validationError);
      } else {
        setError(null);
      }
    }
  };

  const handleStateCreate = async (stateData: PrimeArtifactStateCreate) => {
    if (!selectedArtifact) {
      setError('No artifact selected');
      return;
    }

    try {
      setLoading(true);
      const stateWithArtifact: PrimeArtifactStateCreate = {
        ...stateData,
        prime_artifact_ref: selectedArtifact.id,
      };
      const newState = await PrimeArtifactService.createPrimeArtifactState(stateWithArtifact);
      setSelectedArtifactStates((prev) => [...prev, newState]);

      const transitionType = newTransition.default_transition_type as ArtifactStateTransitionType;

      // If we don't have a from state yet, set this as the from state
      if (!selectedFromState) {
        setSelectedFromState(newState);
        setNewTransition((prev) => ({ ...prev, from_state_ref: newState.id }));

        // For resource transitions, also set this as the to state
        if (transitionType === ArtifactStateTransitionType.RESOURCE) {
          setSelectedToState(newState);
          setNewTransition((prev) => ({ ...prev, to_state_ref: newState.id }));
          setCurrentStep('transition');
        }
      }
      // If we have a from state but no to state, set this as the to state
      else if (!selectedToState) {
        setSelectedToState(newState);
        setNewTransition((prev) => ({ ...prev, to_state_ref: newState.id }));
      }
    } catch (err) {
      setError('Failed to create state');
      console.error('Error creating state:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionCreate = async () => {
    if (!selectedArtifact) {
      setError('No artifact selected');
      return;
    }

    if (!process) {
      setError('No process selected');
      return;
    }

    const transitionType = newTransition.default_transition_type as ArtifactStateTransitionType;

    // Validate state selection based on transition type
    if (transitionType === ArtifactStateTransitionType.CREATION && !selectedToState) {
      setError('Output state is required for creation transitions');
      return;
    }

    if (transitionType === ArtifactStateTransitionType.RESOURCE && !selectedFromState) {
      setError('Resource state is required for resource transitions');
      return;
    }

    if (
      (transitionType === ArtifactStateTransitionType.OPERATIONAL ||
        transitionType === ArtifactStateTransitionType.MATERIAL ||
        transitionType === ArtifactStateTransitionType.IMPROVEMENT) &&
      (!selectedFromState || !selectedToState)
    ) {
      setError('Both input and output states are required for this transition type');
      return;
    }

    // Validate that states are of the same type (operational or improvement)
    if (selectedFromState && selectedToState) {
      const fromStateType = getStateType(selectedFromState.name);
      const toStateType = getStateType(selectedToState.name);
      
      // Only validate if both states are standard states
      if (fromStateType && toStateType && fromStateType !== toStateType) {
        setError(`Cannot create transition: ${selectedFromState.name} (${fromStateType}) and ${selectedToState.name} (${toStateType}) must be the same type`);
        return;
      }
      
      // Also validate using areStatesSameType for non-standard states that might match patterns
      if (!areStatesSameType(selectedFromState.name, selectedToState.name) && 
          (fromStateType || toStateType)) {
        setError(`Cannot create transition: States must be of the same type (both operational or both improvement)`);
        return;
      }
    }

    try {
      setLoading(true);

      // Prepare transition data based on transition type
      const transitionData: PrimeArtifactStateTransition = {
        id: '', // Will be assigned by backend
        name: `Transition for ${selectedArtifact.name}`,
        description: `Transition for ${selectedArtifact.name} in process ${process.name}`,
        prime_artifact_ref: selectedArtifact.id,
        from_state_ref:
          transitionType === ArtifactStateTransitionType.CREATION
            ? ''
            : selectedFromState?.id || '',
        to_state_ref:
          transitionType === ArtifactStateTransitionType.RESOURCE
            ? selectedFromState?.id || ''
            : selectedToState?.id || '',
        prime_process_ref: process.id || '',
        default_transition_type: transitionType,
        is_secondary: newTransition.is_secondary || false,
        transition_parameters: newTransition.transition_parameters || {},
        validation_rules: newTransition.validation_rules || {},
        improvement_ref: null,
        improvement_parameters: {},
        is_improvement: newTransition.is_improvement ?? false,
        created_at: new Date().toISOString(),
        updated_at: '',
        demand_type: newTransition.demand_type || DemandType.TARGET,
        demand_flow_type: newTransition.demand_flow_type || DemandFlowType.DRUM,
      };

      // Set a more descriptive name based on transition type
      if (transitionType === ArtifactStateTransitionType.CREATION) {
        transitionData.name = `Create ${selectedArtifact.name} in ${selectedToState?.name || 'output state'}`;
      } else if (transitionType === ArtifactStateTransitionType.RESOURCE) {
        transitionData.name = `Use ${selectedArtifact.name} in ${selectedFromState?.name || 'resource state'}`;
      } else {
        transitionData.name = `Transition from ${selectedFromState?.name || 'input state'} to ${selectedToState?.name || 'output state'}`;
      }

      console.log('Submitting transition data:', transitionData);

      // Create prime transition
      const transitionToCreate: PrimeArtifactStateTransitionCreate = {
        name: transitionData.name,
        description: transitionData.description || '',
        prime_artifact_ref: selectedArtifact.id,
        prime_process_ref: process.id,
        from_state_ref: selectedFromState?.id || '',
        to_state_ref: selectedToState?.id || '',
        default_transition_type: transitionType,
        is_secondary: newTransition.is_secondary || false,
        transition_parameters: transitionData.transition_parameters || {},
        validation_rules: transitionData.validation_rules || {},
        demand_type: transitionData.demand_type || DemandType.TARGET,
        demand_flow_type: transitionData.demand_flow_type || DemandFlowType.DRUM,
        improvement_ref: null,
        improvement_parameters: {},
        is_improvement: transitionData.is_improvement ?? false,
      };

      const createdTransition = await PrimeArtifactService.createPrimeArtifactTransition(
        selectedArtifact.id,
        transitionToCreate
      );

      // Call the callback with the created transition
      onTransitionCreated(createdTransition);
    } catch (err: unknown) {
      setError('Failed to create transition');
      console.error('Error creating transition:', err);

      // Log the detailed error response
      if ((err as { response?: { data?: unknown; status?: number } }).response) {
        const errorResponse = (err as { response: { data: unknown; status: number } }).response;
        console.error('Error response data:', errorResponse.data);
        console.error('Error response status:', errorResponse.status);

        // Display validation errors if available
        const errorData = errorResponse.data as { detail?: unknown };
        if (errorData && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail
              .map((error: unknown) => {
                const errorObj = error as { loc?: string[]; msg?: string };
                return `${errorObj.loc?.join('.') || 'field'}: ${errorObj.msg || 'validation error'}`;
              })
              .join('\n');
            setError(`Validation errors:\n${errorMessages}`);
          } else {
            setError(`Server error: ${errorData.detail}`);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const renderArtifactStep = () => {
    return (
      <div className={styles.creationStep}>
        <h3>Select or Create Artifact</h3>
        <div className={styles.artifactList}>
          {artifacts.map((artifact) => (
            <div
              key={artifact.id}
              className={styles.artifactItem}
              onClick={() => handleArtifactSelect(artifact)}
            >
              <div className={styles.artifactName}>{artifact.name}</div>
              {artifact.description && (
                <div className={styles.artifactDescription}>{artifact.description}</div>
              )}
            </div>
          ))}
        </div>

        {!showArtifactForm ? (
          <div className={styles.createNew}>
            <h4>Create New Artifact</h4>
            <button onClick={() => setShowArtifactForm(true)}>Create New Artifact</button>
          </div>
        ) : (
          <div className={styles.artifactForm}>
            <h4>Create New Artifact</h4>
            <form onSubmit={handleArtifactFormSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newArtifact.name}
                  onChange={handleArtifactFormChange}
                  placeholder="Enter artifact name"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={newArtifact.description || ''}
                  onChange={handleArtifactFormChange}
                  placeholder="Enter artifact description"
                  rows={3}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Artifact Type</label>
                <div className={styles.artifactTypeDisplay}>{defaultArtifactType.name}</div>
                <input type="hidden" name="artifact_type_id" value={defaultArtifactType.id} />
                <input type="hidden" name="artifact_type_name" value={defaultArtifactType.name} />
              </div>
              <div className={styles.formActions}>
                <button type="button" onClick={() => setShowArtifactForm(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Artifact'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  };

  const renderTransitionTypeStep = () => {
    return (
      <div className={styles.creationStep}>
        <h3>Select Transition Type</h3>
        <p>Choose the type of transition you want to create:</p>

        <div className={styles.transitionTypeOptions}>
          <div
            className={styles.transitionTypeOption}
            onClick={() => handleTransitionTypeSelect(ArtifactStateTransitionType.OPERATIONAL)}
          >
            <h4>Operational</h4>
            <p>Requires both input and output states</p>
          </div>

          <div
            className={styles.transitionTypeOption}
            onClick={() => handleTransitionTypeSelect(ArtifactStateTransitionType.MATERIAL)}
          >
            <h4>Material</h4>
            <p>Requires input state and final state as output</p>
          </div>

          <div
            className={styles.transitionTypeOption}
            onClick={() => handleTransitionTypeSelect(ArtifactStateTransitionType.RESOURCE)}
          >
            <h4>Resource</h4>
            <p>Does not change state, select valid state for transition</p>
          </div>

          <div
            className={styles.transitionTypeOption}
            onClick={() => handleTransitionTypeSelect(ArtifactStateTransitionType.CREATION)}
          >
            <h4>Creation</h4>
            <p>No input state, only output state</p>
          </div>

          <div
            className={styles.transitionTypeOption}
            onClick={() =>
              handleTransitionTypeSelect(ArtifactStateTransitionType.IMPROVEMENT)
            }
          >
            <h4>Improvement</h4>
            <p>Requires both input and output states</p>
          </div>
        </div>
      </div>
    );
  };

  const renderStateStep = () => {
    const transitionType = newTransition.default_transition_type as ArtifactStateTransitionType;

    return (
      <div className={styles.creationStep}>
        <h3>Select States for {selectedArtifact?.name}</h3>
        <p>Transition Type: {transitionType}</p>

        <div className={styles.stateSelection}>
          {transitionType === ArtifactStateTransitionType.CREATION ? (
            // Creation type only needs output state
            <div className={styles.toState}>
              <h4>Output State</h4>
              {selectedToState ? (
                <div className={styles.selectedState}>
                  <div className={styles.stateName}>{selectedToState.name}</div>
                  <button onClick={() => setSelectedToState(null)}>Change</button>
                </div>
              ) : (
                <>
                  <div className={styles.stateList}>
                    {selectedArtifactStates.map((state) => (
                      <div
                        key={state.id}
                        className={styles.stateItem}
                        onClick={() => handleToStateSelect(state)}
                      >
                        <div className={styles.stateName}>{state.name}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => openStateForm('output')}>Create New Output State</button>
                </>
              )}
            </div>
          ) : transitionType === ArtifactStateTransitionType.RESOURCE ? (
            // Resource type only needs one state (same state for input and output)
            <div className={styles.resourceState}>
              <h4>Resource State (used for both input and output)</h4>
              {selectedFromState ? (
                <div className={styles.selectedState}>
                  <div className={styles.stateName}>{selectedFromState.name}</div>
                  <button onClick={() => setSelectedFromState(null)}>Change</button>
                </div>
              ) : (
                <>
                  <div className={styles.stateList}>
                    {selectedArtifactStates.map((state) => (
                      <div
                        key={state.id}
                        className={styles.stateItem}
                        onClick={() => handleFromStateSelect(state)}
                      >
                        <div className={styles.stateName}>{state.name}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => openStateForm('resource')}>
                    Create New Resource State
                  </button>
                </>
              )}
            </div>
          ) : (
            // Process Item, Material, and Persistent Process Item need both input and output states
            <>
              <div className={styles.fromState}>
                <h4>Input State</h4>
                {selectedFromState ? (
                  <div className={styles.selectedState}>
                    <div className={styles.stateName}>{selectedFromState.name}</div>
                    <button onClick={() => setSelectedFromState(null)}>Change</button>
                  </div>
                ) : (
                  <>
                    <div className={styles.stateList}>
                      {selectedArtifactStates.map((state) => (
                        <div
                          key={state.id}
                          className={styles.stateItem}
                          onClick={() => handleFromStateSelect(state)}
                        >
                          <div className={styles.stateName}>{state.name}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => openStateForm('input')}>Create New Input State</button>
                  </>
                )}
              </div>

              <div className={styles.toState}>
                <h4>Output State</h4>
                {selectedToState ? (
                  <div className={styles.selectedState}>
                    <div className={styles.stateName}>{selectedToState.name}</div>
                    <button onClick={() => setSelectedToState(null)}>Change</button>
                  </div>
                ) : (
                  <>
                    <div className={styles.stateList}>
                      {selectedArtifactStates.map((state) => (
                        <div
                          key={state.id}
                          className={styles.stateItem}
                          onClick={() => handleToStateSelect(state)}
                        >
                          <div className={styles.stateName}>{state.name}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => openStateForm('output')}>Create New Output State</button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {((transitionType === ArtifactStateTransitionType.CREATION && selectedToState) ||
          (transitionType === ArtifactStateTransitionType.RESOURCE && selectedFromState) ||
          (selectedFromState && selectedToState)) && (
          <div className={styles.stateSelectionComplete}>
            <button onClick={() => setCurrentStep('transition')}>
              Continue to Transition Parameters
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderTransitionStep = () => {
    return (
      <div className={styles.creationStep}>
        <h3>Define Transition Parameters</h3>

        <div className={styles.transitionParameters}>
          <div className={styles.formGroup}>
            <label htmlFor="demand_type">Demand Type</label>
            <select
              id="demand_type"
              value={newTransition.demand_type}
              onChange={(e) =>
                setNewTransition((prev) => ({ ...prev, demand_type: e.target.value as DemandType }))
              }
            >
              <option value={DemandType.TARGET}>Target</option>
              <option value={DemandType.CAPACITY}>Capacity</option>
              <option value={DemandType.SUPPLY}>Supply</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="demand_flow_type">Demand Flow Type</label>
            <select
              id="demand_flow_type"
              value={newTransition.demand_flow_type}
              onChange={(e) =>
                setNewTransition((prev) => ({
                  ...prev,
                  demand_flow_type: e.target.value as DemandFlowType,
                }))
              }
            >
              <option value={DemandFlowType.DRUM}>Drum</option>
              <option value={DemandFlowType.PUSH}>Push</option>
              <option value={DemandFlowType.PULL}>Pull</option>
            </select>
          </div>
        </div>

        <div className={styles.transitionActions}>
          <button onClick={() => setCurrentStep('state')}>Back</button>
          <button onClick={handleTransitionCreate} disabled={loading}>
            {loading ? 'Creating...' : 'Create Transition'}
          </button>
        </div>
      </div>
    );
  };

  const renderArtifactForm = () => {
    return (
      <div className={styles.artifactFormOverlay}>
        <div className={styles.artifactForm}>
          <h3>Create New Artifact</h3>

          <form onSubmit={handleArtifactFormSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="artifact-name">Artifact Name</label>
              <input
                type="text"
                id="artifact-name"
                name="name"
                value={newArtifact.name}
                onChange={handleArtifactFormChange}
                placeholder="Enter artifact name"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="artifact-description">Description</label>
              <textarea
                id="artifact-description"
                name="description"
                value={newArtifact.description || ''}
                onChange={handleArtifactFormChange}
                placeholder="Enter artifact description"
                rows={3}
              />
            </div>

            <div className={styles.formActions}>
              <button type="button" onClick={() => setShowArtifactForm(false)}>
                Cancel
              </button>
              <button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Artifact'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderStateForm = () => {
    return (
      <div className={styles.stateFormOverlay}>
        <div className={styles.stateForm}>
          <h3>
            Create New{' '}
            {stateFormType === 'input'
              ? 'Input'
              : stateFormType === 'output'
                ? 'Output'
                : 'Resource'}{' '}
            State
          </h3>

          <form onSubmit={handleStateFormSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="state-name">State Name</label>
              <input
                type="text"
                id="state-name"
                name="name"
                value={newState.name}
                onChange={handleStateFormChange}
                placeholder={`Enter ${stateFormType} state name`}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="state-description">Description</label>
              <textarea
                id="state-description"
                name="description"
                value={newState.description || ''}
                onChange={handleStateFormChange}
                placeholder={`Enter ${stateFormType} state description`}
                rows={3}
              />
            </div>

            <div className={styles.formActions}>
              <button type="button" onClick={() => setShowStateForm(false)}>
                Cancel
              </button>
              <button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create State'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.transitionCreationFlow}>
      <div className={styles.flowHeader}>
        <h2>Create New Transition</h2>
        <button className={styles.cancelButton} onClick={onCancel}>
          Cancel
        </button>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {currentStep === 'artifact' && renderArtifactStep()}
      {currentStep === 'transitionType' && renderTransitionTypeStep()}
      {currentStep === 'state' && renderStateStep()}
      {currentStep === 'transition' && renderTransitionStep()}

      {showArtifactForm && renderArtifactForm()}
      {showStateForm && renderStateForm()}
    </div>
  );
};

export default TransitionCreationFlow;
